import { toastManager } from './toast-manager';
import type { ToastOptions } from './types';

function mapOptions(type: string, message: string, options?: ToastOptions) {
  return {
    type,
    title: message,
    ...(options?.description !== undefined && {
      description: options.description,
    }),
    ...(options?.duration !== undefined && { timeout: options.duration }),
    priority:
      type === 'error' || type === 'warning'
        ? ('high' as const)
        : ('low' as const),
    ...(options?.action && {
      actionProps: {
        children: options.action.label,
        onClick: options.action.onClick,
      },
    }),
  };
}

export const toast = {
  success(message: string, options?: ToastOptions): string {
    return toastManager.add(mapOptions('success', message, options));
  },

  error(message: string, options?: ToastOptions): string {
    return toastManager.add(mapOptions('error', message, options));
  },

  warning(message: string, options?: ToastOptions): string {
    return toastManager.add(mapOptions('warning', message, options));
  },

  info(message: string, options?: ToastOptions): string {
    return toastManager.add(mapOptions('info', message, options));
  },

  loading(message: string, options?: Omit<ToastOptions, 'action'>): string {
    return toastManager.add({
      ...mapOptions('loading', message, options),
      timeout: options?.duration ?? 0,
    });
  },

  promise<T>(
    promise: Promise<T>,
    options: {
      loading: { title: string; description?: string };
      success:
        | { title: string; description?: string }
        | ((value: T) => { title: string; description?: string });
      error:
        | { title: string; description?: string }
        | ((err: unknown) => { title: string; description?: string });
    }
  ): Promise<T> {
    return toastManager.promise(promise, options);
  },

  dismiss(id: string): void {
    toastManager.close(id);
  },

  update(
    id: string,
    options: Partial<{
      type: string;
      title: string;
      description: string;
      timeout: number;
    }>
  ): void {
    toastManager.update(id, options);
  },
};
